import { useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Schwebend } from "./Schwebend";
import s from "./auswahl.module.css";

export interface MenuePunkt {
  label: string;
  ausfuehren: () => void;
  /** Rot, mit Trennlinie davor (Löschen) */
  gefahr?: boolean | undefined;
  /** Trennlinie davor, ohne Rot (Abmelden) */
  trenner?: boolean | undefined;
  symbol?: ReactNode | undefined;
  /** Kleiner Zusatz rechts, zum Beispiel „Push, Mail“ */
  hinweis?: string | undefined;
}

interface Props {
  /** Name des Knopfs für Screenreader, ohne `menueLabel` auch der Name des Menüs */
  label: string;
  punkte: readonly MenuePunkt[];
  klasse?: string | undefined;
  /** Inhalt des Knopfs statt ⋯, zum Beispiel das Bild der Person (KOPFZEILE.md, Abschnitt 3.5) */
  knopf?: ReactNode | undefined;
  /** Name des Menüs, wenn er vom Knopf abweicht */
  menueLabel?: string | undefined;
  /** Kopf über den Punkten (Bild, Name, Rollen). Nur zum Ansehen: Was er sagt, gehört in `menueLabel`. */
  kopf?: ReactNode | undefined;
  breite?: number | undefined;
}

/** Menü ⋯ am Ende einer Zeile oder im Kopf des Dialogs (AUFGABEN-LISTE.md, Abschnitt 4), mit eigenem Knopf auch das Personenmenü.
 *  Pfeiltasten bewegen, Enter wählt, Escape schließt und gibt den Fokus an den Knopf zurück. Fest positioniert, damit es in scrollenden
 *  Tabellen nicht abgeschnitten wird. Am Handy unten über die volle Breite (Schwebend). */
export function Menue({ label, punkte, klasse, knopf: inhalt, menueLabel, kopf, breite = 230 }: Props) {
  const knopf = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const [offen, setOffen] = useState(false);

  function knoepfe(): HTMLButtonElement[] {
    return [...(liste.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])];
  }

  function oeffnen() {
    setOffen(true);
    requestAnimationFrame(() => knoepfe()[0]?.focus());
  }

  function taste(e: KeyboardEvent<HTMLDivElement>) {
    const alle = knoepfe();
    const i = alle.indexOf(document.activeElement as HTMLButtonElement);
    const ziel = { ArrowDown: i + 1, ArrowUp: i - 1, Home: 0, End: alle.length - 1 }[e.key];
    if (ziel !== undefined) {
      e.preventDefault();
      alle[(ziel + alle.length) % alle.length]?.focus();
    } else if (e.key === "Tab") {
      setOffen(false);
    }
  }

  return (
    <>
      <button
        ref={knopf}
        type="button"
        className={[s.mehrKnopf, klasse].filter(Boolean).join(" ")}
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-label={label}
        title={inhalt ? undefined : "Mehr"}
        onClick={() => (offen ? setOffen(false) : oeffnen())}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !offen) {
            e.preventDefault();
            oeffnen();
          }
        }}
      >
        {inhalt ?? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="5" cy="12" r="1.8" />
            <circle cx="12" cy="12" r="1.8" />
            <circle cx="19" cy="12" r="1.8" />
          </svg>
        )}
      </button>
      <Schwebend anker={knopf} offen={offen} onSchliessen={() => setOffen(false)} label={menueLabel ?? label} breite={breite} rolle="menu">
        <div ref={liste} className={s.menue} onKeyDown={taste}>
          {kopf ? (
            <div className={s.menueKopf} aria-hidden="true">
              {kopf}
            </div>
          ) : null}
          {punkte.map((p) => (
            <div key={p.label}>
              {p.gefahr || p.trenner ? <hr className={s.menueTrenner} /> : null}
              <button
                type="button"
                role="menuitem"
                className={[s.menuePunkt, p.gefahr && s.menueGefahr].filter(Boolean).join(" ")}
                onClick={() => {
                  setOffen(false);
                  knopf.current?.focus();
                  p.ausfuehren();
                }}
              >
                {p.symbol}
                {p.label}
                {p.hinweis ? <small className={s.menueHinweis}>{p.hinweis}</small> : null}
              </button>
            </div>
          ))}
        </div>
      </Schwebend>
    </>
  );
}

/** Papierkorb für „löschen“ in Menüs und Zeilen */
export const PAPIERKORB = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </svg>
);
